import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { eCriteriaVariation } from '../../services/stepper/steps/search-stepper';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Order } from 'projects/reg-hub-common/src/lib/interfaces/orders/order';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { EnumeratedTypesRepositoryService, instantValidationThrottle, Lien, OrderManagerService, ValidationProblem, ValidationService } from 'reg-hub-common';
import { SteppedComponent } from 'projects/reg-hub-client/src/interfaces/stepped-component';
import { StepValidationState } from 'projects/reg-hub-client/src/interfaces/step';

@Component({
    selector: 'app-registration-number-search-criteria',
    templateUrl: './registration-number-search-criteria.component.html',
    styleUrl: './registration-number-search-criteria.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RegistrationNumberSearchCriteriaComponent extends SteppedComponent {
  @Input() isFormEnabled: boolean = true;
  @Input() criteriaVariations: eCriteriaVariation[] = [];

  protected shouldDisplaySearchFromDate: boolean = false;
  protected shouldDisplayExactSimilar: boolean = false;
  protected searchCriteriaDetails!: FormGroup;
  protected hasUserExpandedAdditionalParameters = false;

  private orderForValidation: Order | null = null;

  protected registrationNumberError$ = new BehaviorSubject<string | null | undefined>(null);
  protected searchFromDateError$ = new BehaviorSubject<string | null | undefined>(null);

  constructor(
    private formBuilder: FormBuilder,
    orderManager: OrderManagerService,
    private enumeratedTypesRepo: EnumeratedTypesRepositoryService,
    validationService: ValidationService) {
    super(orderManager, validationService);
  }

  protected override init(order: Order): void {
    super.init(order);

    this.shouldDisplaySearchFromDate = this.criteriaVariations.includes(eCriteriaVariation.SearchFromDate);
    this.shouldDisplayExactSimilar = this.criteriaVariations.includes(eCriteriaVariation.ExactSimilar);

    const lien = this.order.lien;
    this.searchCriteriaDetails = this.formBuilder.group({
      registrationNumber: [lien?.registrationNumber],
    });

    if (!this.isFormEnabled) {
      this.searchCriteriaDetails.disable();
    }

    this.observableErrors$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(errors => this.pushErrors(errors));
    this.searchCriteriaDetails.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        instantValidationThrottle())
      .subscribe(() => this.validate(this.getOrderForValidation()));
  }

  public override onSaving(): void {
    super.onSaving();

    const lien = this.order.lien;

    if (!lien) {
      return;
    }

    this.updateLien(lien);
    //this.updateSearchParameters(this.order);
  }

  public override pushErrors(errors: ValidationProblem[] | undefined): void {
    if (!errors) {
      return;
    }

    this.registrationNumberError$.next(errors.filter(error => error.path.includes('registrationnumber'))?.at(0)?.userFriendlyMessage);
    this.searchFromDateError$.next(errors.filter(error => error.path.includes('searchfromdate'))?.at(0)?.userFriendlyMessage);

  }

  public override getValidationState(errors: ValidationProblem[] | undefined, order: Order): StepValidationState {
    return (errors?.length ?? 0) === 0 ? StepValidationState.ValidationSuccess : StepValidationState.ValidationError;
  }

  private getOrderForValidation(): Order {
    if (!this.orderForValidation) {
      this.orderForValidation = this.copyOrder(this.order);
    }

    const lien = this.order.lien;

    if (!lien) {
      return this.orderForValidation!;
    }

    this.updateLien(lien);
    this.orderForValidation.lien = lien;
    //this.updateSearchParameters(this.orderForValidation!);

    return this.orderForValidation!;
  }

  private updateLien(lien: Lien) {
    lien.registrationNumber = this.searchCriteriaDetails.get('registrationNumber')!.value;
  }

  // This may come in to play for ON...
  // private updateSearchParameters(order: Order) { 
  //   if (this.shouldDisplaySearchFromDate) {
  //     order.searchParameters!.searchFromDate = this.searchCriteriaDetails.get('searchFromDate')!.value;
  //   }

  //   if(this.shouldDisplayExactSimilar) {
  //     order.searchParameters!.exactOnlyResults = this.searchCriteriaDetails.get('exactSimilar')!.value;
  //   }
  // }

  // shouldExpandAdditionalParameters(): boolean {
  //   const searchFromDateControl = this.searchCriteriaDetails.get('searchFromDate');

  //   return this.hasUserExpandedAdditionalParameters || (searchFromDateControl && searchFromDateControl.value);
  // }
}
